import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';

import { pageTitles } from '@/constants/pageTitles';
import useReactQuery from '@/hooks/common/useReactQuery';
import useCurrentUser from '@/hooks/store/useCurrentUser';
import useToast from '@/hooks/store/useToast';
import {
  type ParentNameChangeFormValues,
  type ParentWithStudents,
  type ProfileEditAuthState,
} from '@/interfaces/parent';

import MyPageTemplate2 from './MyPageTemplate2';
import ParentNameChangeForm from './ParentNameChangeForm';

const ParentNameChangePage: React.FC = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentParent] = useCurrentUser();

  useEffect(() => {
    const state = location.state as ProfileEditAuthState | null;

    if (!state?.authenticated) {
      navigate('/mypage/profile', { replace: true });
      return;
    }
  }, [location.state, navigate]);

  const initialValues: ParentNameChangeFormValues = {
    last_name: currentParent?.last_name || '',
    first_name: currentParent?.first_name || '',
    last_name_kana: currentParent?.last_name_kana || '',
    first_name_kana: currentParent?.first_name_kana || '',
  };

  const { useMutation, useQuery } = useReactQuery();

  const { refetch: refetchMe } = useQuery<ParentWithStudents>({
    queryKey: ['me'],
    url: '/api/school/parents/me',
    enabled: false,
  });

  const { mutateAsync, isPending, error } = useMutation({
    method: 'PATCH',
    url: '/api/school/parents/me_without_additional_auth',
    onSuccess: async () => {
      // 保護者情報のキャッシュを再フェッチ
      await refetchMe();
      showToast('アカウント情報を保存しました');
      navigate('/mypage/profile');
    },
  });

  const submitErrorMessages =
    error && error.json
      ? error.json.message || '予期しないエラーが発生しました。'
      : '';

  const handleSubmit = (formValues: ParentNameChangeFormValues) => {
    const trimmedValues = {
      last_name: formValues.last_name.trim(),
      first_name: formValues.first_name.trim(),
      last_name_kana: formValues.last_name_kana.trim(),
      first_name_kana: formValues.first_name_kana.trim(),
    };
    void mutateAsync(JSON.stringify({ parent: trimmedValues }));
  };

  const handleCancel = () => {
    navigate('/mypage/profile');
  };

  return (
    <MyPageTemplate2>
      <Helmet>
        <title>{pageTitles.mypage}</title>
      </Helmet>
      <ParentNameChangeForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isPending}
        serverError={submitErrorMessages}
      />
    </MyPageTemplate2>
  );
};

export default ParentNameChangePage;
